import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ButtonContainer from '../components/ButtonContainer';
import queryString from 'query-string';
import TallButton from '../components/TallButton';
import PageTitle from '../components/PageTitle';
import CancelDeletion from '../components/CancelDeletion';
import { Link, useLocation } from 'react-router-dom';
import { Flag, URLParameters } from '../types';
import { addFlag } from '../actions/flag-actions';
import messages from './CancelAccountDeletionPage.messages';
import dayjs from 'dayjs';
import WhiteboxPage from '../components/WhiteboxPage';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';
import Heading from '@atlaskit/heading';

const pageId = 'cancelAccountDeletionPage';

export async function cancelAccountDeletionLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/login/cancel-account-deletion/state', request, {
    capabilities: ['capability-group--manage-profile', 'capability-name--cancel-account-deletion'],
  });
}

export const CancelAccountDeletionPageComponent = () => {
  const { search } = useLocation();
  const { formatMessage } = useIntl();
  const navigate = useNavigateSoftOrHard();

  const deleteOn = useAppSelector(state => state.cancelDeletion.deleteOn);
  const dispatch = useAppDispatch();

  const cancelDeletion = useCallback(() => {
    const { token } = queryString.parse(search) as URLParameters;

    return fetch(`/rest/cancel-account-deletion${search}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
      }),
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw response;
        }
      })
      .then(({ redirectTo }) => {
        navigate(redirectTo);
      })
      .catch(() => {
        const flag: Flag = {
          type: 'error',
          title: messages.cancelAccountDeletionFlagErrorTitle,
          description: messages.cancelAccountDeletionFlagErrorDesc,
        };

        dispatch(addFlag(flag.type, flag.title, flag.description));
      });
  }, [dispatch, search, navigate]);

  const signupLink = '/signup' + search;
  const loginLink = '/login' + search;

  return (
    <WhiteboxPage
      pageId={pageId}
      header={
        <Heading as="h1" size="small">
          <FormattedMessage {...messages.headTitle} />
        </Heading>
      }
      footer={
        <Link id="signup-for-new-account" to={signupLink}>
          <FormattedMessage {...messages.cancelAccountDeletionSignupMessage} />
        </Link>
      }
      whiteboxFooter={
        <Link to={loginLink}>
          <FormattedMessage {...messages.cancelAccountDeletionLogout} />
        </Link>
      }>
      <PageTitle title={formatMessage(messages.headTitle)} />
      <CancelDeletion
        message={
          <FormattedMessage
            {...messages.cancelAccountDeletionInfo}
            tagName="p"
            values={{ deleteOn: dayjs(deleteOn).format('DD MMMM YYYY') }}
          />
        }
      />
      <ButtonContainer>
        <TallButton
          appearance="primary"
          id="login-submit"
          type="submit"
          isFullWidth
          onClick={cancelDeletion}>
          <FormattedMessage {...messages.cancelAccountDeletionButton} />
        </TallButton>
      </ButtonContainer>
    </WhiteboxPage>
  );
};

export default CancelAccountDeletionPageComponent;
